import { useEffect, useState } from "react";
import { API_BASE } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

function createEmptyDiagram(remediationType, tableType) {
  return {
    remediation_type: remediationType,
    table_type: tableType,
    chartKind: "empty",
    data: [],
  };
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeStepData(rows = []) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => ({
    step: String(row.step_id ?? row.step ?? row.label ?? "unknown"),
    value: toNumber(row.amount ?? row.value ?? 0, 0),
  }));
}

function normalizeDailyTrendData(rows = []) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => ({
    day: row.day ?? row.date ?? row.label ?? "",
    openPerDay: toNumber(
      row.open_per_day ?? row.openPerDay ?? row.open_cases ?? row.open ?? 0,
      0
    ),
    closedPerDay: toNumber(
      row.closed_per_day ?? row.closedPerDay ?? row.closed_cases ?? row.closed ?? 0,
      0
    ),
    totalOpenCases: toNumber(
      row.total_open_cases ??
      row.totalOpenCases ??
      row.total_open ??
      row.total ??
      0,
      0
    ),
  }));
}

function normalizeDiagramPayload(raw, remediationType, tableType) {
  const payload =
    raw?.data && raw?.remediation_type ? raw : raw?.data || raw || {};

  const resolvedTableType = payload?.table_type ?? tableType;
  const dataRows = payload?.data || [];

  if (resolvedTableType === "CASES_PER_DAY") {
    return {
      remediation_type: payload.remediation_type ?? remediationType,
      table_type: resolvedTableType,
      chartKind: "daily-line",
      data: normalizeDailyTrendData(dataRows),
    };
  }

  if (resolvedTableType === "MTTR") {
    return {
      remediation_type: payload.remediation_type ?? remediationType,
      table_type: resolvedTableType,
      chartKind: "step-bar-days",
      data: normalizeStepData(dataRows),
    };
  }

  if (resolvedTableType === "OVERVIEW_PERCENT") {
    return {
      remediation_type: payload.remediation_type ?? remediationType,
      table_type: resolvedTableType,
      chartKind: "step-bar-percent",
      data: normalizeStepData(dataRows),
    };
  }

  return {
    remediation_type: payload.remediation_type ?? remediationType,
    table_type: resolvedTableType,
    chartKind: "step-bar-total",
    data: normalizeStepData(dataRows),
  };
}

export function useDiagramData(searchSpace, remediationType, tableType) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [diagram, setDiagram] = useState(
    createEmptyDiagram(remediationType, tableType)
  );

  useEffect(() => {
    let isCancelled = false;

    if (searchSpace !== "in" || !remediationType || !tableType) {
      setLoading(false);
      setStatus("");
      setDiagram(createEmptyDiagram(remediationType, tableType));
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setStatus("");
      setDiagram(createEmptyDiagram(remediationType, tableType));

      try {
        const params = new URLSearchParams({
          remediation_type: remediationType,
          table_type: tableType,
        });

        const res = await fetchWithAuth(
          `${API_BASE}/ainstein/in/diagram-by-type?${params.toString()}`
        );

        if (!res.ok) {
          throw new Error(`Could not load diagram (${res.status})`);
        }

        const raw = await res.json();
        const normalized = normalizeDiagramPayload(
          raw,
          remediationType,
          tableType
        );

        if (!isCancelled) {
          setDiagram(normalized);
        }
      } catch (error) {
        if (!isCancelled) {
          setDiagram(createEmptyDiagram(remediationType, tableType));
          setStatus(error?.message || "Failed to load diagram data.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [searchSpace, remediationType, tableType]);

  return {
    loading,
    status,
    diagram,
  };
}